import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { builtinViewId, randomScreenFrontDoor,
  templateForSetup, templateForViewId,
  WALL_VIEW_ID,
} from "@gooner/shared";
import { becomeScreen } from "../lib/becomeScreen";
import { apiErrorOf } from "../lib/api";
import { trackGoldGate } from "../lib/gold";
import { useSession } from "../lib/session";
import { usePendingScreen } from "../lib/usePendingScreen";
import { canOpenOnPendingScreen, rememberHomeView } from "../lib/lastHomeView";
import { type ScreenOrigin, screenOriginOf } from "../lib/screenOrigin";
import { BackToViews } from "../components/BackToViews";
import { rekeyScreenSettings } from "../lib/screenSettings";
import { useAnalyticsRole } from "../lib/useAnalyticsRole";
import { CodeCard } from "../components/CodeCard";
import { GoldDialog } from "../control/components/GoldDialog";
import { DisplayApp } from "./DisplayApp";
import { IconBack } from "../control/ui/icons";

/**
 * How often THIS page asks whether somebody has claimed it — the pairing page, where
 * somebody is standing in front of the television mid-claim, so much faster than the
 * ambient ten seconds the site bar polls at everywhere else.
 */
const POLL_MS = 5000;

/**
 * Query keys this page (and DisplayApp under it) actually read. Anything else —
 * Casey 2026-10-07 AM #2's `?mode=bogus&foo=1` — is dropped with replace, same
 * manners as Browse's bogus page/sort clamp (!116).
 */
const SCREEN_QUERY_KEYS = new Set(["view", "setup", "code", "buy"]);

/**
 * A screen with no device yet: it shows a code and waits to be claimed.
 *
 * This is the inversion. The screen used to ask the operator to walk over and type six
 * characters on a TV remote, once per screen. Now it displays, and the controller — the
 * device that actually has an account to attach a screen to — reads.
 *
 * That is the direction every TV platform uses. YouTube, Netflix, Plex and AirPlay all put
 * the code on the TV and have you enter it on a phone or a computer; WhatsApp and Discord
 * put it on the desktop and have the phone scan it, because there the phone is the account.
 * The rule is the same in both: whoever has no account displays, whoever has one reads.
 *
 * There was a QR here as well, and it was the primary path: the phone's camera app opened
 * `/claim/:code` and the pairing happened in a browser tab the operator never chose. It is
 * gone from THIS card, and typing is the flow this page describes.
 *
 * The reason is that it was never really in the product. Scanning happened in the phone's
 * CAMERA APP — this repo has no scanner, only `qrcode-generator`, which draws and cannot
 * read — so the fastest path led out of the app and back in through a cold tab. One
 * mechanism that the Goontroller owns end to end beats two where the better one is a
 * bounce through iOS. It also collapses the instructions: every surface that explains
 * pairing now has one sentence to write instead of two.
 *
 * WHAT THAT ARGUMENT DID NOT COVER is a queue. It was written about setting a screen up —
 * one operator, one television, done once — where a code read off the glass and typed into
 * the phone already in your hand is genuinely the shorter path. At a party there is no
 * operator to hand a code to, every guest is holding a different phone, and "point your
 * camera at the TV" is the only instruction that survives being said across a room to the
 * fifth person. So the QR exists again as PartyBadge: off by default, switched on
 * deliberately from this screen's own settings, and never on this card. Typing is still
 * what every surface explains.
 *
 * IT PLAYS WHILE IT WAITS. The code used to sit on a near-black page, so choosing "Screen"
 * on the front door bought you a black rectangle and a four-character code — the least
 * appealing thing this app can put on a television, at the exact moment somebody is
 * deciding whether it is worth setting up. The Community Wall (or the quick setup the
 * URL asked for) now runs underneath and the code sits over it on a scrim.
 *
 * That merges the two things the front door was asking you to choose between: "watch
 * something now" and "make this a screen" are the same act here, and the second one no
 * longer costs you the first while you walk to your phone.
 *
 * The view is rendered WITHOUT a device id, which is a supported shape rather than a
 * trick: a display socket with a view and no device is "an anonymous viewer of a view"
 * (see ws.ts), which is exactly what this is until somebody claims it. No device row is
 * created, so nothing is left behind if the tab is closed unclaimed.
 *
 * THE CODE IS A CARD, NOT A BLANKET. It used to be a full-bleed scrim: `inset-0` carrying
 * a heavy tint and a blur, on the reasoning that a four-character code has to be read
 * across a room against moving video and legibility beats letting the clips through.
 *
 * The legibility argument was right; the scope was wrong. Blanking the whole television
 * hides the one thing that makes this worth setting up, at exactly the moment somebody is
 * deciding whether it is — the quick setup is playing underneath the entire time and nobody
 * can see it. So the tint and the blur moved off the full-bleed layer and onto a bounded
 * card: the same near-opaque backing exactly where the text is, and the clips visible
 * everywhere else.
 *
 * The layer that remains only POSITIONS the card, and is `pointer-events-none` so it does
 * not swallow clicks for the video it no longer hides. A layer that covers the screen
 * invisibly is still covering the screen.
 *
 * The × dismisses it for anyone who would rather just watch, and the SCREEN BAR brings it
 * back — because a code you cannot get to again is a screen you cannot pair.
 *
 * That way back used to be a pill of this screen's own, and it had already been moved once:
 * from the top right, where at 390px it landed directly on the control bar's Goon button,
 * down to the bottom right, on the reasoning that the two should own opposite edges. The
 * real answer was that they should not be two things. The code is a segment of the bar now
 * — leftmost, one press from the card whether the bar is open or folded — so there is no
 * second floating object left to collide with anything.
 */
export function PendingScreen({ frontDoor = false }: { frontDoor?: boolean } = {}) {
  // Also set by the DisplayApp this renders; repeated here so the code-shown event this
  // page's own hook sends is labelled even on a commit where the player is not yet up.
  useAnalyticsRole("screen");
  const [params, setParams] = useSearchParams();
  // Set by a Quick setup's Play on Views (QuickSetupCard). History state, not a query
  // param, so shared or typed `/screen` links never grow a Back they did not come from.
  const location = useLocation();
  const origin = screenOriginOf(location.state);
  const fromViews = origin.from === "views";
  /*
   * The origin every rewrite of this entry has to restate (see lib/screenOrigin), in a
   * ref so the callbacks below read today's without being rebuilt by it. The front door
   * stamps `home` here: a tab that becomes a device from `/` is still "Home" to the
   * logo, and the device page is where its later mode switches are remembered.
   */
  const originRef = useRef<ScreenOrigin>(origin);
  originRef.current = frontDoor ? { ...origin, home: true } : origin;
  // The take-it-back deep-link's target square, captured ONCE at mount before the playing
  // DisplayApp scrubs it — so it can be carried across the become-a-device redirect below
  // (query params are otherwise lost when a signed-in screen converts). Read here, applied
  // there, like `code` and `view`.
  const [buyOnce] = useState(() => params.get("buy"));
  // Drop unknown query keys so junk does not linger in shared / bookmarked URLs.
  useEffect(() => {
    const next = new URLSearchParams(params);
    let dirty = false;
    for (const key of [...next.keys()]) {
      if (!SCREEN_QUERY_KEYS.has(key)) {
        next.delete(key);
        dirty = true;
      }
    }
    if (dirty) setParams(next, { replace: true, state: originRef.current });
  }, [params, setParams]);
  /*
   * THE WELCOME CARD IS GONE. `/` mounted this page with an intro dialog (the old
   * landing hero + role picker) for one release; the unified ☰ made it redundant —
   * every role and page it offered is one press away on every surface — so the front
   * door is simply the playing wall now. What `frontDoor` still means is the one
   * difference that survived: `/` does not greet with the pairing code.
   */
  /* One view for the life of this screen, picked before first paint.
     Not re-picked on re-render: this component re-renders on every poll tick, and a
     setup that changed every two seconds would be a slideshow of layouts. That is also why
     `params` is read here but NOT in the dependency list — the URL is an opening argument,
     not a live binding. */
  const initialViewId = useMemo(
    () => {
      /*
       * The setup / view the click asked for, or a default if it did not ask.
       *
       * Clicking a quick setup lands here rather than on a minted device, so the id has to
       * survive the trip — otherwise picking "Latinas ×3" would put a random setup on the
       * television, which is a worse answer than the one the operator gave.
       *
       * Bare `/` (frontDoor) opens the Community Wall — the documented front-door default
       * — so Home is a PLACE, not a dice roll each visit (Casey 2026-10-07 AM #2). Bare
       * `/screen` still draws one of the three SCREEN_FRONT_DOORS (Build a Slut /
       * Colorwave / Community Wall). Every quick setup is one tap away in the picker.
       *
       * Falls back rather than erroring on an unknown id: this is a URL a person can type
       * on a TV remote, and a stale or fat-fingered `?setup=` should still leave them with
       * something playing and a code to pair. There is nothing here worth a 404.
       *
       * `?view=` FIRST, for a temporary id or a wall. Pressing Edit copies what is playing
       * and writes the copy here (see onForked below) before this tab leaves for the
       * editor, so that coming back — the editor's "Back to screen", or the browser's own
       * Back — remounts this page playing the copy rather than the wall it started on.
       * A wall id is the OTHER door: the Premium Wall needs naming to be opened on a fresh
       * screen — the Discord feed's link and the How-To's "walk onto" do exactly that. A
       * saved id typed here by hand would be a screen playing somebody's library view
       * without a device, which nothing wants, so anything else falls through to the setup
       * and the default.
       *
       * A QUICK SETUP BY ID COUNTS TOO. The picker's prev/next writes `view=builtin:<id>`
       * here (onForked below), and before this the `builtin:` kind was not on the list,
       * so F5 after "Next view" fell through to the STALE `?setup=` the page opened with
       * (2026-10-06 QA: Tit Ladder → next ×2 → Around the World → F5 → Tit Ladder). Only
       * a `builtin:` id that names a real template — anything else still falls through.
       */
      const asked = params.get("view");
      // Infinity is a third door — the picker's row and the How-To's link both name it.
      if (asked && canOpenOnPendingScreen(asked)) return asked;
      // Offered setups only, plus SETUP_ALIASES: a retired id (`safe-for-work`) silently
      // opens its replacement (Fully Clothed); a hidden or unknown id falls through.
      const setup = templateForSetup(params.get("setup"));
      if (setup) return builtinViewId(setup.id);
      // `/` is a PLACE: Community Wall (WALL_VIEW_ID — "the front door's default"),
      // not a fresh draw every visit (Casey 2026-10-07 AM #2). `/screen` keeps the
      // three-door lottery — that path is "typed to pair", not Home.
      return frontDoor ? WALL_VIEW_ID : randomScreenFrontDoor();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  /*
   * A `?view=` THAT CANNOT PLAY HERE SAYS SO. It used to fall through silently to the
   * setup or the wall above, so `/screen?view=zzzz-not-a-view` played the Community Wall
   * with no word (2026-10-06 QA) — "the link worked" when it had not. `?setup=` keeps its
   * quiet fallback (a fat-fingered TV-remote setup id still gets something playing); a
   * named VIEW is a link somebody followed, and /views/<bogus> already answers it with
   * "Couldn't open this view". A real quick-setup id (`builtin:`) is left to the
   * fallthrough as before, so this decides nothing about which of those wins.
   * Read once, like initialViewId.
   */
  const [viewMissing] = useState(() => {
    const asked = params.get("view");
    return !!asked && asked !== initialViewId && templateForViewId(asked) === null;
  });
  /*
   * Whether the pairing card is open on arrival: ONLY WHEN A LINK ASKS (`?code=1`).
   *
   * It used to greet a bare `/screen` (and `?setup=`) at desktop widths, on the argument
   * that a TV browser typing /screen came to pair. Product call (2026-10-21): the card
   * never opens on its own — on a first visit the surface's intro (SurfaceGuide) is what
   * greets you, and a code card over it was the stacked-dialog first impression. The
   * code is not lost, only un-volunteered: the bar's code chip (with its hint) never
   * folds on a playing page, so the card is one press away at every width.
   *
   * Explicit doors still open it: `?code=1` (or any value but "0") on the URL, and the
   * become-a-device redirect below, which carries an ALREADY-open card across as
   * `?code=1` so nothing on the glass changes. `?code=0` and the front door stay closed.
   */
  const [showCode, setShowCode] = useState(() => {
    // The front door is where a visitor lands to WATCH.
    if (frontDoor) return false;
    const asked = params.get("code");
    if (asked === "0") return false;
    if (asked !== null) return true;
    // Unasked: closed, at every width. The chip is the door.
    return false;
  });
  /*
   * What is on this screen, which is the wall (or asked-for setup) until Edit copies it.
   *
   * State rather than a constant, because the bar's Edit button is on every screen now,
   * including this one. Neither the wall nor a built-in can be written, so pressing Edit
   * takes a temporary copy — and this panel has no device row to retarget (it is an
   * anonymous viewer until somebody claims it), so DisplayApp hands the copy back and
   * the swap happens here.
   *
   * The FIRST view is still the shared one by id, not a copy: a private duplicate would
   * not see later edits (or, on the wall, later SALES), and copying on mount would mint
   * a temporary view for every tab that ever opened this page.
   */
  const [playingViewId, setPlayingViewId] = useState(initialViewId);
  // Logo / Menu Home restore this after a trip through Browse (Casey 2026-10-07 AM #1).
  // THE FRONT DOOR ONLY: `/screen` is "typed to pair", and remembering what it played
  // made Home a quick setup after any visit to it — against "bare `/` is a PLACE".
  useEffect(() => {
    if (frontDoor) rememberHomeView(playingViewId);
  }, [frontDoor, playingViewId]);
  /*
   * The copy goes into the URL as well as into state, IN PLACE. Edit navigates this tab to
   * the editor a moment after handing the copy back, and this page remounts from scratch
   * on the way back with its state gone — so the history entry underneath the editor has
   * to say what was playing. `replace`, because a screen that pressed Edit and a screen
   * playing the copy are the same place; a pushed entry would make Back a two-step trip
   * through the wall it had already left. Read back by initialViewId above.
   */
  const onForked = useCallback(
    (id: string) => {
      setPlayingViewId(id);
      setParams(
        (p) => {
          p.set("view", id);
          // `?setup=` was the opening argument and has been consumed; left in the URL it
          // reads as a second, contradictory answer to "what is this screen playing".
          p.delete("setup");
          return p;
        },
        // The origin rides along: without it the first Next dropped "Back to views".
        { replace: true, state: originRef.current },
      );
    },
    [setParams],
  );
  /*
   * The claimable identity itself lives in usePendingScreen — the same announce, poll
   * and lapse-recovery the site bar's bubble runs on every other page, just on this
   * page's faster clock. On claim the hook navigates this tab to the device it became.
   *
   * It is told what is PLAYING, so redeeming the code inherits the quick setup rather than
   * replacing it. Claiming a screen must not change what is on the screen.
   */
  const { code, error, party } = usePendingScreen(POLL_MS, undefined, playingViewId, {
    party: true,
    origin: originRef.current,
  });

  /*
   * A SIGNED-IN SCREEN REGISTERS ITSELF, and stops being a pending screen.
   *
   * The code exists to attach a screen that has no account to somebody who has one. A tab
   * that is signed in has skipped that problem — it already knows whose screen it is — so
   * making it wait to be discovered is asking a question it has the answer to. Before this,
   * every /screen tab was an anonymous viewer of a view with no device row at all, which
   * meant it could not appear in any rail: open four of them on your own account and your
   * remote listed none, because none of them existed as devices.
   *
   * It carries the view it is already playing, so nothing on the glass changes as it
   * converts — the tab simply becomes /view/d/:id showing the same quick setup, and turns
   * up on the remote a beat later with no code typed.
   *
   * It keeps a code either way: a claimed screen wears an ADOPT code in the bar's bubble
   * (see DeviceScreen), so every tab is still claimable by whoever can read it — this
   * changes who a screen belongs to by default, not whether it can change hands.
   *
   * KEYED ON THE USER, so signing in on a television that is already open converts it
   * then; guarded by a ref so it happens once. A FAILURE LEAVES IT PENDING on purpose —
   * at the account's screen ceiling the server answers 409, and the honest response is to
   * go on being a claimable screen rather than to strand a television on an error.
   *
   * Signed OUT it does nothing, and that is not an oversight: an anonymous identity lives
   * in this browser's storage, so a device minted here would be owned by the television
   * and could never appear on the phone's remote. The code is the only thing that crosses
   * that gap, which is exactly what it is for.
   */
  const user = useSession((s) => s.user);
  const navigate = useNavigate();
  const registering = useRef(false);
  // The screens cap, when becoming a device is refused: with the cap counting live
  // screens this only fires when three genuinely hold sockets, which is a real gate and
  // gets the real dialog. It used to be swallowed below with nothing on the glass at all.
  const [goldOpen, setGoldOpen] = useState(false);
  // Read at conversion time, not tracked: whether the card is open is carried across as
  // an opening argument (`?code=1`, see usePairing in DeviceScreen), and re-running this
  // effect on every × press would be wrong in every way.
  const showCodeRef = useRef(showCode);
  showCodeRef.current = showCode;
  useEffect(() => {
    // Not from the not-found card: there is nothing playing to become a screen OF.
    if (!user || registering.current || viewMissing) return;
    registering.current = true;
    void becomeScreen(playingViewId)
      // `replace`, so Back leaves rather than re-entering a page this tab has stopped
      // being — the same reasoning as the claim path in usePendingScreen. The card stays
      // as it was: nothing on the glass changes as this tab becomes a device.
      .then((device) => {
        // The OTHER door onto a device, so it owes the same re-key the claim path does
        // (rekeyToDevice in usePendingScreen): a screen configured while it waited must
        // still be configured a frame later when it is a device.
        rekeyScreenSettings(device.id);
        // Carry the take-it-back target through the redirect: the device's board reads
        // `?buy` and opens that square. `?code=1` keeps the pairing card open across the
        // conversion; the two combine when both are present.
        const q = new URLSearchParams();
        if (showCodeRef.current) q.set("code", "1");
        if (buyOnce) q.set("buy", buyOnce);
        const qs = q.toString();
        navigate(`/view/d/${device.id}${qs ? `?${qs}` : ""}`, {
          replace: true,
          // Where this screen came from survives the conversion — DeviceScreen reads it
          // for the same "Back to views" and for what Home remembers.
          state: originRef.current,
        });
      })
      .catch((e: unknown) => {
        // At the ceiling, or offline. Stay a pending screen: the code still works, and
        // the next mount of this page — or a sign-out and back in — gets another go.
        registering.current = false;
        if (apiErrorOf(e)?.goldUpsell) {
          trackGoldGate("screens");
          setGoldOpen(true);
        }
      });
  }, [user, playingViewId, navigate, buyOnce, viewMissing]);

  if (viewMissing) return <ScreenViewMissing id={params.get("view") ?? ""} />;

  /*
   * "‹ Back to views" — only when Views sent us here (a Quick setup now opens in this tab
   * and says so in history state; Casey 2026-10-09 #2). Direct `/screen` links, the front
   * door and a typed URL carry no state, so they get exactly the bar they always had.
   */
  const back = fromViews ? <BackToViews /> : null;

  return (
    <div className="relative h-[100dvh] w-screen overflow-hidden bg-[var(--color-ink)]">
      {/* Playing underneath, with no device of its own. The code rides the site bar's
          right-side bubble — the same one every page wears — and the big card is one
          press away behind it. */}
      <DisplayApp
        viewId={playingViewId}
        pairing={{
          // The placeholder rather than nothing: the bubble is the same size before and
          // after the code lands, so the bar does not reflow the moment it arrives.
          text: code ?? "····",
          shown: showCode,
          onToggle: () => setShowCode((s) => !s),
        }}
        onForked={onForked}
        party={party}
        back={back}
      />

      {/* The big code card — shared chrome now (see components/CodeCard), because the
          bubble opens it on every page, not just this one. No `becomeScreen` here: this
          page already IS the screen. */}
      {showCode ? <CodeCard code={code} error={error} onClose={() => setShowCode(false)} /> : null}
      {goldOpen ? <GoldDialog source="screens" onClose={() => setGoldOpen(false)} /> : null}
    </div>
  );
}

/** The /screen twin of ViewEditor's ViewMissing: same words, same way back. */
function ScreenViewMissing({ id }: { id: string }) {
  return (
    <div className="flex h-[100dvh] w-screen flex-col items-center justify-center bg-[var(--color-ink)] px-6 text-center">
      <h2 className="text-lg font-semibold text-zinc-100">Couldn&rsquo;t open this view</h2>
      <p className="mt-2 max-w-md text-sm text-zinc-500">
        <span className="break-all">{id}</span>: there is no view by that id that can play on a
        screen.
      </p>
      <p className="mt-2 max-w-md text-xs text-zinc-600">
        Unsaved views are temporary — one that was never saved to your library doesn&rsquo;t
        outlive the session.
      </p>
      <Link
        to="/views"
        className="mt-6 inline-flex items-center gap-2 rounded-lg border border-[var(--color-edge)] px-3.5 py-2 text-sm text-zinc-200 hover:bg-[var(--color-panel-2)]"
      >
        <IconBack /> Back to views
      </Link>
    </div>
  );
}
